import template from './pitchPanel.html';
import updateDialogTemplate from './pitchUpdateDialog.html';
import pitchUpdateCtrl from './pitchUpdateCtrl';
import pitchStoryDialogTemplate from './pitchStoryDialog.html';
import pitchStoryDialogCtrl from './pitchStoryDialogCtrl';
import '../../../sentryConfig';

function PitchPanelController(
  $location,
  $log,
  $scope,
  $uibModal,
  $translate,
  $q,
  platformService,
  Publication,
  refineStateService,
  siteService,
  collectionsService,
  featureFlag,
  toastWrapperService
) {
  const ctrl = this;

  const setWebsiteDisplayName = (pitch) => {
    const currentPitch = pitch;
    const currentSite = this.sites.find(site => site._id === pitch.website)
      || { display_name: $translate.instant('UNKNOWN') };
    currentPitch.siteName = currentSite.display_name;
    return currentPitch;
  };

  ctrl.aria_pitchToPlatform = `${$translate.instant('PITCH_STORY_TO')} ${$translate.instant('PLATFORMS')}`;
  ctrl.aria_pitchToPublication = `${$translate.instant('PITCH_STORY_TO')} ${$translate.instant('PUBLICATIONS')}`;

  $scope.expandedPlat = refineStateService.isExpanded('STORY_DRAWER_PLATFORM') === null ? true :
    refineStateService.isExpanded('STORY_DRAWER_PLATFORM');
  $scope.expandedPub = refineStateService.isExpanded('STORY_DRAWER_PUBLICATION') === null ? true :
    refineStateService.isExpanded('STORY_DRAWER_PUBLICATION');
  $scope.expandedCollections = refineStateService.isExpanded('STORY_DRAWER_COLLECTIONS') === null ? true :
    refineStateService.isExpanded('STORY_DRAWER_COLLECTIONS');

  $scope.iconPlat = $scope.expandedPlat ? 'minus' : 'plus';
  $scope.iconPub = $scope.expandedPub ? 'minus' : 'plus';
  $scope.iconCollections = $scope.expandedCollections ? 'minus' : 'plus';

  $scope.$watch('expandedPlat', refineStateService.curriedSet('STORY_DRAWER_PLATFORM'));
  $scope.$watch('expandedPub', refineStateService.curriedSet('STORY_DRAWER_PUBLICATION'));
  $scope.$watch('expandedCollections', refineStateService.curriedSet('STORY_DRAWER_COLLECTIONS'));

  $scope.toggleExpanded = (tab) => {
    $scope[`expanded${tab}`] = !$scope[`expanded${tab}`];
    $scope[`icon${tab}`] = $scope[`expanded${tab}`] ? 'minus' : 'plus';
  };

  this.$onInit = () => {
    // Disable collections for these wapo source types, so that videos can be added to collections
    this.disableCollections = $location.host().includes('washpost') && (this.cms === 'methode' || this.cms === 'wordpress');

    const requests = [
      platformService.getAll(),
      Publication.getNumberOfPublications(),
      siteService.getWebsites()
    ];

    if (!this.disableCollections) {
      requests.push(collectionsService.getUsage(this.storyId));
    }

    $q.all(requests).then(([
      platforms,
      pubsCount,
      sites,
      collections = []
    ]) => {
      this.numberOfExistingPlatforms = platforms.length;
      this.platforms = platforms;
      this.numberOfExistingPublications = pubsCount;
      this.sites = sites;
      this.setCollections({ collections });

      this.platformSites = sites
        .filter(site =>
          (platforms.findIndex(plat => plat.website === site._id) > -1));

      if (Array.isArray(this.pitches.platforms) && this.pitches.platforms.length > 0) {
        this.pitches.platforms = this.pitches.platforms
          .map(pitch => setWebsiteDisplayName(pitch));
      }
    });

    // initialize action in progress to false
    this.actionInProgress = false;
  };

  this.getExcludedPlatforms = () => {
    if (!this.pitches.platforms) {
      return [];
    }

    return this.pitches.platforms
      .reduce((acc, pitch) => {
        if (pitch.currentStatus !== 'withdrawn') {
          acc.push({
            path: pitch.planPath,
            website: pitch.website
          });
        }

        return acc;
      }, []);
  };

  this.getExcludedPublications = () => {
    if (!ctrl.pitches.publications) {
      return [];
    }

    return ctrl.pitches.publications
      .map(pitch => pitch.publicationId);
  };

  this.edit = (pitch) => {
    $uibModal.open({
      template: updateDialogTemplate,
      controller: 'pitchUpdateCtrl',
      controllerAs: '$ctrl',
      windowClass: 'pitchEditModal',
      resolve: {
        pitch() {
          return pitch;
        }
      }
    }).result
      .then((updatedPitch) => {
        let pitchResult = updatedPitch;
        const targetType = `${updatedPitch.type}s`;
        const targetIndex = ctrl.pitches[targetType].indexOf(pitch);

        if (targetType === 'platforms') {
          pitchResult = setWebsiteDisplayName(updatedPitch);
        }

        ctrl.pitches[targetType][targetIndex] = pitchResult;
      }, angular.noop);
  };

  this.openCollectionsModal = () => {
    const { storyId, storyWebsite } = this;

    $uibModal.open({
      component: 'collectionsModal',
      size: 'lg',
      resolve: {
        storyId() {
          return storyId;
        },
        storyWebsite() {
          return storyWebsite;
        }
      }
    })
      .result
      .then(() => {
        if (!this.disableCollections) {
          collectionsService.getUsage(this.storyId)
            .then((collections) => {
              this.setCollections({ collections });
            });
        }
      });
  };

  this.openModal = (type) => {
    $uibModal.open({
      template: pitchStoryDialogTemplate,
      controller: 'pitchStoryDialogCtrl',
      controllerAs: '$ctrl',
      size: 'lg',
      windowClass: 'pitch-story-dialog',
      resolve: {
        sites() {
          return ctrl.sites;
        },
        platforms() {
          return ctrl.platforms;
        },
        platformSites() {
          return ctrl.platformSites;
        },
        type() {
          return type;
        },
        storyId() {
          return ctrl.storyId;
        },
        excludedDestinations() {
          return type === 'platforms' ? ctrl.getExcludedPlatforms() : ctrl.getExcludedPublications();
        }
      }
    }).result.then((data) => {
      if (!ctrl.pitches[type]) {
        ctrl.pitches[type] = [];
      }

      if (data) {
        data.forEach((item) => {
          let pitch = item;
          if (type === 'platforms') {
            pitch = setWebsiteDisplayName(item);
          }

          ctrl.pitches[type].push(pitch);
        });
      }
    });
  };

  this.removeFromCollection = (collectionId) => {
    this.actionInProgress = true;
    let errorMsg = $translate.instant('ERROR_REMOVING_CONTENT_FROM_COLLECTION');
    collectionsService.removeContentFromCollection(collectionId, this.storyId)
      .then(({ data }) => {
        if (data && data.message === 'removed') {
          const collections = ctrl.collections.filter(coll => coll.id !== collectionId);
          this.setCollections({ collections });
        } else {
          $log.error(data);
          toastWrapperService.createToast(errorMsg, 'danger');
        }
        this.actionInProgress = false;
      }).catch((error) => {
        if (error.data && error.data.code) {
          errorMsg = $translate.instant(error.data.code);
        }
        $log.error(error);
        toastWrapperService.createToast(errorMsg, 'danger');
        this.actionInProgress = false;
      });
  };

  this.moveToTop = (collectionId) => {
    this.actionInProgress = true;
    let errorMsg = $translate.instant('ERROR_UPDATING_ORDER');
    collectionsService
      .moveToTop(collectionId, this.storyId)
      .then((resp) => {
        if (resp.status && resp.status === 200) {
          toastWrapperService.createToast(
            $translate.instant('SUCCESS'),
            'success'
          );
        } else {
          $log.error(resp);
          toastWrapperService.createToast(errorMsg, 'danger');
        }
        this.actionInProgress = false;
      })
      .catch((error) => {
        if (error.data && error.data.code) {
          errorMsg = $translate.instant(error.data.code);
        }
        $log.error(error);
        toastWrapperService.createToast(errorMsg, 'danger');
        this.actionInProgress = false;
      });
  };

  this.moveToBottom = (collectionId) => {
    this.actionInProgress = true;
    let errorMsg = $translate.instant('ERROR_UPDATING_ORDER');
    collectionsService
      .moveToBottom(collectionId, this.storyId)
      .then((resp) => {
        if (resp.status && resp.status === 200) {
          toastWrapperService.createToast(
            $translate.instant('SUCCESS'),
            'success'
          );
        } else {
          $log.error(resp);
          toastWrapperService.createToast(errorMsg, 'danger');
        }
        this.actionInProgress = false;
      })
      .catch((error) => {
        if (error.data && error.data.code) {
          errorMsg = $translate.instant(error.data.code);
        }
        $log.error(error);
        toastWrapperService.createToast(errorMsg, 'danger');
        this.actionInProgress = false;
      });
  };
}

const pitchPanel = {
  template,
  bindings: {
    storyId: '<',
    storyWebsite: '<',
    cms: '<',
    collections: '<',
    contentType: '<',
    pitches: '=',
    setCollections: '&'
  },
  controller: 'PitchPanelController'
};

const controllerArray = [
  '$location',
  '$log',
  '$scope',
  '$uibModal',
  '$translate',
  '$q',
  'platformService',
  'Publication',
  'refineStateService',
  'siteService',
  'collectionsService',
  'featureFlag',
  'toastWrapperService',
  PitchPanelController
];


export default function (ngModule) {
  pitchStoryDialogCtrl(ngModule);
  pitchUpdateCtrl(ngModule);
  ngModule.controller('PitchPanelController', controllerArray);
  ngModule.component('pitchPanel', pitchPanel);
}
